/**
 * `Date` is emitted as ISO 8601 to avoid `Date.prototype.toString` producing locale-formatted
 * output like `"Sun Apr 26 2026 00:00:00 GMT+0900 (한국 표준시)"`, which is non-URL-safe and
 * not directly parseable on the server.
 *
 * `null` is preserved as the literal string `"null"` to match prior `${value}` interpolation
 * behavior; callers that want to omit a value should pass `undefined` instead.
 */
function serializeQueryValue(value: unknown): string {
  if (value instanceof Date) {
    return value.toISOString();
  }
  return String(value);
}

/**
 * Builds a URL-encoded query string from a DTO object.
 *
 * - `undefined` values are dropped; `null` becomes the literal `"null"` (see `serializeQueryValue`).
 * - Arrays are emitted as repeated keys following NestJS' default convention: `key=v1&key=v2`.
 *
 * Prefer this over templating values directly into a URL — `${value}` invokes `toString()` on
 * objects (notably `Date`), which produces locale-dependent, non-URL-safe output.
 */
export function buildQueryString(dto: Record<string, unknown>): string {
  return Object.entries(dto)
    .filter(([, value]) => value !== undefined)
    .flatMap(([key, value]) => {
      const encodedKey = encodeURIComponent(key);
      if (Array.isArray(value)) {
        return value.map((entry) => `${encodedKey}=${encodeURIComponent(serializeQueryValue(entry))}`);
      }
      return `${encodedKey}=${encodeURIComponent(serializeQueryValue(value))}`;
    })
    .join('&');
}
